/** One bounded poster encoder shared by local selection and video previews. */
const POSTER_MAX_DIMENSION = 320;
const POSTER_MAX_BYTES = 512 * 1024;

const encodePoster = (source: CanvasImageSource, width: number, height: number): Promise<Blob | null> => {
  const scale = Math.min(1, POSTER_MAX_DIMENSION / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(width * scale / 2) * 2;
  canvas.height = Math.floor(height * scale / 2) * 2;
  if (canvas.width < 2 || canvas.height < 2) return Promise.resolve(null);
  const context = canvas.getContext('2d');
  if (!context) return Promise.resolve(null);
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((blob) => {
    resolve(blob?.type === 'image/webp' && blob.size <= POSTER_MAX_BYTES ? blob : null);
  }, 'image/webp', 0.6));
};

const captureVideoPoster = (url: string, timestamp = 0): Promise<Blob | null> => new Promise((resolve) => {
  const video = document.createElement('video');
  let settled = false;
  let encoding = false;
  const finish = (poster: Blob | null) => {
    if (settled) return;
    settled = true;
    clearTimeout(timeout);
    video.onloadeddata = null;
    video.onloadedmetadata = null;
    video.onseeked = null;
    video.onerror = null;
    video.removeAttribute('src');
    video.load();
    resolve(poster);
  };
  const capture = () => {
    if (settled || encoding || video.readyState < 2) return;
    encoding = true;
    try {
      encodePoster(video, video.videoWidth, video.videoHeight).then(finish, () => finish(null));
    } catch { finish(null); }
  };
  const timeout = setTimeout(() => finish(null), 15_000);
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.onloadedmetadata = () => {
    if (timestamp > 0) {
      const target = Math.min(timestamp, Math.max(0, video.duration - 0.001));
      if (target > 0) video.currentTime = target;
      else timestamp = 0;
    }
  };
  // The first decoded frame needs neither a duration scan nor a seek event.
  video.onloadeddata = () => { if (timestamp <= 0) capture(); };
  video.onseeked = capture;
  video.onerror = () => finish(null);
  video.src = url;
  video.load();
});

/** Decode once while the actual media transfer proceeds independently. */
export async function extractLocalMediaPoster(file: File): Promise<Blob | null> {
  if (typeof URL.createObjectURL !== 'function' || file.type.startsWith('audio/')) return null;
  const url = URL.createObjectURL(file);
  try {
    if (file.type.startsWith('image/')) {
      return await new Promise<Blob | null>((resolve) => {
        const image = new Image();
        const timeout = setTimeout(() => { image.src = ''; resolve(null); }, 15_000);
        image.onload = async () => {
          clearTimeout(timeout);
          try { resolve(await encodePoster(image, image.naturalWidth, image.naturalHeight)); }
          catch { resolve(null); }
        };
        image.onerror = () => { clearTimeout(timeout); resolve(null); };
        image.src = url;
      });
    }
    return await captureVideoPoster(url);
  } finally { URL.revokeObjectURL(url); }
}

export const posterDataUrl = (blob: Blob | null): Promise<string | null> => {
  if (!blob) return Promise.resolve(null);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
};

// Frame Extraction Utility
// Extracts a single frame from a video at a specific timestamp

/**
 * Extract a frame from an existing video element
 * @param video - The video element already loaded in the DOM
 * @param timestamp - Time in seconds to extract the frame
 * @returns Promise with base64 data URL of the frame
 */
export async function extractFrameFromVideoElement(
  video: HTMLVideoElement,
  timestamp: number
): Promise<string | null> {
  return new Promise((resolve) => {
    if (!video || video.readyState < 2) {
      console.warn('[FrameExtractor] Video not ready');
      resolve(null);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('[FrameExtractor] No canvas context');
      resolve(null);
      return;
    }

    // Store current time to restore later
    const originalTime = video.currentTime;
    const wasPlaying = !video.paused;

    // Pause if playing
    if (wasPlaying) {
      video.pause();
    }

    const handleSeeked = () => {
      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw current frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

        // Restore original time and playing state
        video.currentTime = originalTime;
        if (wasPlaying) {
          video.play().catch(console.error);
        }

        // Clean up event listener
        video.removeEventListener('seeked', handleSeeked);

        resolve(dataUrl);
      } catch (error) {
        console.error('[FrameExtractor] Failed to extract frame:', error);

        // Restore state even on error
        video.currentTime = originalTime;
        if (wasPlaying) {
          video.play().catch(console.error);
        }
        video.removeEventListener('seeked', handleSeeked);

        resolve(null);
      }
    };

    // Add seeked event listener
    video.addEventListener('seeked', handleSeeked);

    // Seek to target time
    video.currentTime = Math.min(timestamp, video.duration);
  });
}

/**
 * Extract a frame from a video URL at a specific timestamp
 * @param videoUrl - The video URL (signed URL)
 * @param timestamp - Time in seconds to extract the frame
 * @returns Promise with base64 data URL of the frame
 */
export async function extractFrameFromVideo(
  videoUrl: string,
  timestamp: number
): Promise<string | null> {
  return new Promise((resolve) => {
    console.log('[FrameExtractor] Starting extraction:', { videoUrl: videoUrl.substring(0, 50), timestamp });

    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('[FrameExtractor] No canvas context');
      resolve(null);
      return;
    }

    // First-party bounded media is served from the API origin with CORS. Opt in
    // before assigning src so canvas extraction remains untainted when the web
    // application and API use different subdomains.
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    // Check if this is a GCS URL - if so, we can't extract frames
    if (videoUrl.includes('storage.googleapis.com')) {
      console.warn('[FrameExtractor] Cannot extract frames from GCS URLs due to CORS');
      resolve(null);
      return;
    }

    const cleanup = () => {
      video.src = '';
      video.load();
    };

    video.onerror = (e) => {
      console.error('[FrameExtractor] Video error:', e);
      cleanup();
      resolve(null);
    };

    video.onloadedmetadata = () => {
      console.log('[FrameExtractor] Metadata loaded, duration:', video.duration);
      // Seek to the target timestamp
      video.currentTime = Math.min(timestamp, video.duration);
    };

    video.onseeked = () => {
      console.log('[FrameExtractor] Seeked to time:', video.currentTime);
      // Video has seeked to the target time, now extract the frame
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        console.log('[FrameExtractor] Frame extracted successfully');
        cleanup();
        resolve(dataUrl);
      } catch (error) {
        console.error('[FrameExtractor] Failed to extract frame:', error);
        cleanup();
        resolve(null);
      }
    };

    video.src = videoUrl;
    video.load();
  });
}

// Cache for extracted frames to avoid redundant extractions
const frameCache = new Map<string, string>();

/**
 * Get a frame from video with caching
 * @param videoUrl - The video URL
 * @param timestamp - Time in seconds
 * @returns Promise with base64 data URL of the frame
 */
export async function getCachedFrame(
  videoUrl: string,
  timestamp: number
): Promise<string | null> {
  const cacheKey = `${videoUrl}-${timestamp.toFixed(2)}`;

  if (frameCache.has(cacheKey)) {
    return frameCache.get(cacheKey) || null;
  }

  const frame = await extractFrameFromVideo(videoUrl, timestamp);

  if (frame) {
    frameCache.set(cacheKey, frame);

    // Limit cache size to 50 frames
    if (frameCache.size > 50) {
      const firstKey = frameCache.keys().next().value;
      frameCache.delete(firstKey);
    }
  }

  return frame;
}
